// What the pointer landed on (the plan's C.4, the port of
// `main.ts:1334-1341`).
//
// **The order is the caller's job.** A click is answered in this order and
// no other:
//
//   1. the minimap, which is a picture over the map and takes the click
//      whole (`render/minimap.ts` `hit`);
//   2. a fleet, at `PICK_FLEET_PX` divided by the camera scale, and only
//      at a zoom the fleet layer draws at;
//   3. a site, at `PICK_SITE_PX` divided by the camera scale.
//
// `hitTest` below does steps 2 and 3 in that order; the input module asks
// the minimap first, and only calls this when the minimap did not take it.
//
// Two rules hold for every pick here:
//
//   - **A pick reads the SMOOTHED position**, which is what the player sees
//     — never the last position the server sent.
//   - **A pick never reads the cull bit.** A hull the viewport hid is still
//     under the pointer if the pointer is over it, which is §10 rule 7: the
//     culling takes away drawing, never meaning. `pickFleet` therefore has
//     ONE gate, the reveal alpha, because a contact the sonar has barely
//     found is not clickable (§5).
//
// The radii scale with the zoom, so the clickable target keeps its size on
// SCREEN: at zoom 2 a fleet is picked within 10 world px, at zoom 0.5
// within 40.

import { SiteKind, type Site } from '../gen/proto/aquaphobia';
import {
  CAMERA_DETAIL_MIN_ZOOM, PICK_FLEET_PX, PICK_SITE_PX, SNAP_DEPOSIT_PX,
} from '../gen/presentation';
import type { TapTarget } from '../ui/touch';
import { snapDepositAt } from './snap';

/** What a fleet pick reports. The shape `main.ts`'s handlers already read. */
export interface FleetPick {
  id: number;
  own: boolean;
  faction: number;
  name: string;
}

/** The one method `hit.ts` needs of a fleet store. */
export interface FleetPicker {
  pick(x: number, y: number, radius: number): FleetPick | null;
}

/** The two methods `hit.ts` needs of a site store. */
export interface SitePicker {
  pick(x: number, y: number, radius: number): Site | null;
  pickDeposit(x: number, y: number, radius: number): Site | null;
}

/**
 * What the pointer landed on, in the shape the command code reads.
 *
 * A battle mark of the chart (R548) is not a hit: the chart is asked AFTER
 * the fleets and the sites, through `commands.pressMark` (R702), because a
 * mark records a fight that is over and must never take a press the live
 * map wants.
 */
export type Hit =
  | { kind: 'fleet'; id: number; own: boolean; faction: number; name: string }
  | { kind: 'site'; id: number };

/** The nearest fleet under a world point, or null. */
export function pickFleet(
  fleets: FleetPicker, wx: number, wy: number, scale: number,
): FleetPick | null {
  return fleets.pick(wx, wy, PICK_FLEET_PX / scale);
}

/** The nearest site under a world point, or null. */
export function pickSite(
  sites: SitePicker | null | undefined, wx: number, wy: number, scale: number,
): Site | null {
  return sites?.pick(wx, wy, PICK_SITE_PX / scale) ?? null;
}

/**
 * The nearest deposit under a world point, or null — the right-click snap
 * target for haulers (R80). Its radius is tighter than a site pick's,
 * because the snap changes the ORDER the click gives and must not fire on a
 * click the player meant for open water.
 */
export function pickDeposit(
  sites: SitePicker | null | undefined, wx: number, wy: number, scale: number,
): Site | null {
  return snapDepositAt(sites, wx, wy, SNAP_DEPOSIT_PX / scale);
}

/**
 * A fleet, then a site. Fleets take precedence, because a hull sits on top
 * of the site it is over and the player is pointing at what they can see.
 *
 * The caller must have asked the minimap first.
 *
 * **The zoom LOD gate is the one gate of the fleets** (R456, R501). Below
 * `CAMERA_DETAIL_MIN_ZOOM` the fleet layer is not drawn, so it must not
 * answer a pick either: a hit on a mark nobody can see reads as a broken
 * map. Sites stay pickable, because that layer draws at every zoom. The
 * gate is the camera's scale and not the cull bit, so the rule above holds.
 * The season map and the arcade match both pick here, so both read it.
 */
export function hitTest(
  fleets: FleetPicker,
  sites: SitePicker | null | undefined,
  wx: number, wy: number, scale: number,
): Hit | null {
  if (scale >= CAMERA_DETAIL_MIN_ZOOM) {
    const f = pickFleet(fleets, wx, wy, scale);
    if (f) return { kind: 'fleet', id: f.id, own: f.own, faction: f.faction, name: f.name };
  }
  const s = pickSite(sites, wx, wy, scale);
  if (s) return { kind: 'site', id: s.id };
  return null;
}

/** Is this site the hold of this player? */
export function isOwnBase(site: Site | undefined, playerId: number): boolean {
  return site?.kind === SiteKind.SITE_KIND_PLAYER_BASE && site.ownerPlayer === playerId;
}

/**
 * What a tap landed on, in the vocabulary `ui/touch.ts` decides with. The
 * season map and the arcade match both answer the tap grammar with it.
 * `site` is the record of a site hit, which each caller reads from its own
 * stores.
 */
export function tapTargetOf(hit: Hit | null, site: Site | undefined, playerId: number): TapTarget {
  if (hit?.kind === 'fleet') return { kind: 'fleet', own: hit.own };
  if (hit?.kind !== 'site') return { kind: 'water' };
  if (site?.kind === SiteKind.SITE_KIND_DEPOSIT) return { kind: 'deposit' };
  return isOwnBase(site, playerId) ? { kind: 'ownBase' } : { kind: 'site' };
}
